/**
 * Base API Client
 *
 * Centralized API client that eliminates duplication across all API files.
 * Provides consistent URL building, headers, request/response handling, and error management.
 *
 * ✅ DRY: Single source of truth for API request patterns
 * ✅ Separation of Concerns: Pure data access layer
 * ✅ Type Safety: Full TypeScript support
 * ✅ Consistent Error Handling: Unified error responses
 * ✅ Observability: Uses api-fetch-wrapper for logging
 *
 * @file base-api-client.ts
 * @version 1.0.0
 */

import {getApiEndpoint} from '@/shared/config/environment';
import type {ApiFetchOptions} from './api-fetch-wrapper';
import {apiFetchJson, apiPost} from './api-fetch-wrapper';

/**
 * Base API Client Configuration
 */
export interface BaseApiClientConfig {
  /**
   * Base endpoint path (e.g., 'orders', 'cart', 'photos')
   * Will be prefixed with /api/v1/
   */
  basePath: string;

  /**
   * Optional custom base URL (overrides getApiEndpoint)
   * Use only for external services or special cases
   */
  customBaseUrl?: string;
}

/**
 * Request options for API calls
 */
export interface ApiRequestOptions extends Omit<ApiFetchOptions, 'method' | 'body'> {
  /**
   * Skip JSON content-type header (for FormData, etc.)
   */
  skipJsonContentType?: boolean;

  /**
   * Custom headers to merge with defaults
   */
  headers?: Record<string, string>;
}

/**
 * Base API Client Class
 *
 * Provides common patterns for all API clients:
 * - URL building
 * - Header management
 * - Request/response handling
 * - Error handling
 *
 * Note: Methods are public to allow composition pattern (e.g., CheckoutApiClient
 * composing multiple BaseApiClient instances). Subclasses can still override.
 */
export class BaseApiClient {
  protected readonly baseUrl: string;
  protected readonly basePath: string;

  constructor(config: BaseApiClientConfig) {
    this.basePath = config.basePath;
    this.baseUrl = config.customBaseUrl || getApiEndpoint(config.basePath);
  }

  /**
   * Build full URL from endpoint path
   *
   * @param endpoint - Endpoint path (e.g., 'by-session', 'items/123')
   * @returns Full URL
   *
   * @example
   * client.buildUrl('by-session') // => 'http://localhost:8000/api/v1/orders/by-session'
   * client.buildUrl('items/123')  // => 'http://localhost:8000/api/v1/orders/items/123'
   */
  buildUrl(endpoint = ''): string {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const separator = this.baseUrl.endsWith('/') ? '' : '/';
    return normalizedEndpoint ? `${this.baseUrl}${separator}${normalizedEndpoint}` : this.baseUrl;
  }

  /**
   * Make a GET request
   *
   * @param endpoint - Endpoint path
   * @param options - Request options
   * @returns Parsed JSON response
   */
  async get<T>(endpoint = '', options: ApiRequestOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);
    return apiFetchJson<T>(url, {
      method: 'GET',
      ...enhancedOptions,
    });
  }

  /**
   * Make a POST request with JSON body
   *
   * @param endpoint - Endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param options - Request options
   * @returns Parsed JSON response
   */
  async post<T>(
    endpoint = '',
    body: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);
    return apiPost<T>(url, body, enhancedOptions);
  }

  /**
   * Make a PUT request with JSON body
   *
   * @param endpoint - Endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param options - Request options
   * @returns Parsed JSON response
   */
  async put<T>(
    endpoint = '',
    body: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);
    return apiFetchJson<T>(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...enhancedOptions.headers,
      },
      body: JSON.stringify(body),
      ...enhancedOptions,
    });
  }

  /**
   * Make a PATCH request with JSON body
   *
   * @param endpoint - Endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param options - Request options
   * @returns Parsed JSON response
   */
  async patch<T>(
    endpoint = '',
    body: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);
    return apiFetchJson<T>(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...enhancedOptions.headers,
      },
      body: JSON.stringify(body),
      ...enhancedOptions,
    });
  }

  /**
   * Make a DELETE request
   *
   * @param endpoint - Endpoint path
   * @param options - Request options
   * @returns Parsed JSON response (if any)
   */
  async delete<T>(endpoint = '', options: ApiRequestOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);
    return apiFetchJson<T>(url, {
      method: 'DELETE',
      ...enhancedOptions,
    });
  }

  /**
   * Make a POST request with FormData
   *
   * @param endpoint - Endpoint path
   * @param formData - FormData object
   * @param options - Request options
   * @returns Parsed JSON response
   */
  async postFormData<T>(
    endpoint = '',
    formData: FormData,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const url = this.buildUrl(endpoint);
    const enhancedOptions = await this.getRequestOptions(options);

    // Don't set Content-Type for FormData (browser will set it with boundary)
    // Remove Content-Type from headers if present
    const headers: Record<string, string> = {};
    if (enhancedOptions.headers) {
      Object.keys(enhancedOptions.headers).forEach((key) => {
        if (key.toLowerCase() !== 'content-type') {
          const value = enhancedOptions.headers![key];
          if (value !== undefined) {
            headers[key] = value;
          }
        }
      });
    }

    const fetchOptions: Parameters<typeof apiFetchJson<T>>[1] = {
      method: 'POST',
      body: formData,
      ...enhancedOptions,
    };

    // Only add headers if we have any
    if (Object.keys(headers).length > 0) {
      fetchOptions.headers = headers;
    }

    return apiFetchJson<T>(url, fetchOptions);
  }

  /**
   * Protected method to get request options
   * Subclasses can override this to add auth headers, custom headers, etc.
   *
   * @param options - Base request options
   * @returns Enhanced request options (Promise to support async auth checks)
   */
  protected async getRequestOptions(options: ApiRequestOptions = {}): Promise<ApiRequestOptions> {
    // Return options synchronously by default
    // Subclasses can override to add async auth headers
    return Promise.resolve(options);
  }
}
